import { type Color } from '@backend/lib/db/typeRoots/color'
import { type GradientStop, type LinearGradient } from '@backend/lib/db/typeRoots/layoutNode'
import { colorToHex } from '@/components/modules/CanvasEditor/color'

/** Power-sigmoid easing: t^p / (t^p + (1-t)^p).
 *  p=1 is linear, higher p creates a stronger S-curve that holds
 *  each color flat before a sharp midpoint transition. */
function ease(t: number, smoothness: number): number {
  if (smoothness === 0) return t
  const p = 1 + (smoothness / 100) * 5
  const tp = Math.pow(t, p)
  return tp / (tp + Math.pow(1 - t, p))
}

/** Expand gradient stops, inserting intermediate sub-stops for any pair where the
 *  outgoing stop has smoothness > 0. This produces soft ease-in-out transitions
 *  that work with both CSS linear-gradient and Canvas addColorStop. */
export function expandStops(stops: GradientStop[]): GradientStop[] {
  const sorted = [...stops].sort((a, b) => a.position - b.position)
  if (sorted.length < 2) return sorted

  const result: GradientStop[] = [sorted[0]!]
  const SUB_STEPS = 10

  for (let i = 0; i < sorted.length - 1; i++) {
    const curr = sorted[i]!
    const next = sorted[i + 1]!
    const s = curr.smoothness ?? 0

    if (s > 0) {
      for (let j = 1; j < SUB_STEPS; j++) {
        const t = j / SUB_STEPS
        const eased = ease(t, s)
        result.push({
          color: interpolateColor(curr.color, next.color, eased),
          position: curr.position + (next.position - curr.position) * t,
        })
      }
    }

    result.push(next)
  }

  return result
}

export function gradientToCss(gradient: LinearGradient): string {
  const stops = expandStops(gradient.stops)
    .map((s) => `${colorToHex(s.color)} ${s.position * 100}%`)
    .join(', ')
  return `linear-gradient(${gradient.angle}deg, ${stops})`
}

export function createDefaultGradient(color: Color): LinearGradient {
  return {
    angle: 90,
    stops: [
      { color, position: 0 },
      { color: { r: 255, g: 255, b: 255, a: 255 }, position: 1 },
    ],
  }
}

export function interpolateColor(c1: Color, c2: Color, ratio: number): Color {
  return {
    r: Math.round(c1.r + (c2.r - c1.r) * ratio),
    g: Math.round(c1.g + (c2.g - c1.g) * ratio),
    b: Math.round(c1.b + (c2.b - c1.b) * ratio),
    a: Math.round(c1.a + (c2.a - c1.a) * ratio),
  }
}

export function getColorAtPosition(stops: GradientStop[], position: number): Color {
  const sorted = [...stops].sort((a, b) => a.position - b.position)

  if (sorted.length === 0) return { r: 0, g: 0, b: 0, a: 255 }
  if (position <= sorted[0]!.position) return sorted[0]!.color
  if (position >= sorted[sorted.length - 1]!.position) return sorted[sorted.length - 1]!.color

  for (let i = 0; i < sorted.length - 1; i++) {
    const curr = sorted[i]!
    const next = sorted[i + 1]!
    if (position >= curr.position && position <= next.position) {
      const ratio = (position - curr.position) / (next.position - curr.position)
      return interpolateColor(curr.color, next.color, ratio)
    }
  }

  return sorted[sorted.length - 1]!.color
}
